import { useContext, useCallback } from 'react';

import { AjaxContext, AppContext, UserContext } from 'commons-ui';
import { UserDetailsContext } from 'context/userDetails';
import { PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';
import { EURL } from '../../../../enum';
import { ADMIN_USER_ACTION_OWNERS, ADMIN_USER_ACTIONS } from './constants';

type BlockUnblockUserPropsT = {
	reason: string;
	attachments: string[];
	adminActionType: string;
	freshdeskTicketId: string;
};
type UserLevelT = {
	userLevel: number;
};
const useService = () => {
	const {
		app: { appName }
	} = useContext(AppContext);
	const { postData } = useContext(AjaxContext);
	const {
		selectedUser: { uid, zupeeUserId, ludoUserId }
	} = useContext(UserDetailsContext);

	const { user } = useContext(UserContext);

	const unsetPhone = useCallback(async (behaviour: String) => {
		const result = await postData({
			endpoint: '/crm/userDetails/userSummary/unsetPhone',
			queryParams: {
				appName
			},
			body: { uid, behaviour }
		});

		return result;
	}, []);

	const unsetAccountNumber = useCallback(async () => {
		const result = await postData({
			endpoint: `/dashboard/unsetBankAccount/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-unsetBankAccount`,
			queryParams: {
				appName
			},
			body: {
				uid,
				agentName: user?.userName,
				agentID: user?.email,
				adminActionType: ADMIN_USER_ACTIONS.DEACTIVATE_USER_BANK,
				actionOwner: ADMIN_USER_ACTION_OWNERS.CRM
			}
		});

		return result;
	}, []);

	const unsetUpiId = useCallback(async () => {
		const result = await postData({
			endpoint: `/dashboard/unsetUpiId/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-unsetUpiId`,
			queryParams: {
				appName
			},
			body: {
				uid,
				agentName: user?.userName,
				agentID: user?.email,
				adminActionType: ADMIN_USER_ACTIONS.DEACTIVATE_USER_VPA,
				actionOwner: ADMIN_USER_ACTION_OWNERS.CRM
			}
		});

		return result;
	}, []);

	const blockUnblockUser = useCallback(async (props: BlockUnblockUserPropsT) => {
		const { reason, attachments, adminActionType, freshdeskTicketId } = props;
		const result = await postData({
			endpoint: `/dashboard/blockUnblockUser/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-blockUnblockUser`,
			queryParams: {
				appName
			},
			body: {
				agentName: user?.userName,
				reason,
				attachments,
				zupeeUserId,
				ludoUserId,
				adminActionType,
				freshdeskTicketId
			}
		});

		return result;
	}, []);

	const updateUserFraudStatus = useCallback(async (props: BlockUnblockUserPropsT) => {
		const { reason, attachments, adminActionType, freshdeskTicketId } = props;
		const result = await postData({
			endpoint: `/dashboard/updateUserFraudStatus/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-updateUserFraudStatus`,
			queryParams: {
				appName
			},
			body: {
				reason,
				attachments,
				uid,
				adminActionType,
				freshdeskTicketId,
				agentName: user?.userName
			}
		});

		return result;
	}, []);

	const updateUserLevel = useCallback(async (props: UserLevelT) => {
		const { userLevel } = props;
		const result = await postData({
			endpoint: `/dashboard/updateUserLevel/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-updateUserLevel`,
			queryParams: {
				appName
			},
			body: {
				zupeeUserId: uid,
				newLevel: userLevel,
				updatedBy: ''
			}
		});

		return result;
	}, []);

	const updateAlternatePhone = useCallback(async ({ alternatePhoneNumber }: { alternatePhoneNumber: string }) => {
		const result = await postData({
			endpoint: `/dashboard/updateAlternatePhoneNumber/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-updateAlternatePhoneNumber`,
			queryParams: {
				appName
			},
			body: {
				zupeeUserId: uid,
				alternatePhoneNumber
			}
		});

		return result;
	}, []);

	const cancelSubscription = useCallback(async () => {
		const result = await postData({
			endpoint: EURL.CANCEL_SUBSCRIPTION,
			queryParams: {
				appName
			},
			body: {
				zupeeUserId
			}
		});

		return result;
	}, [zupeeUserId, appName]);

	return {
		unsetPhone,
		unsetAccountNumber,
		unsetUpiId,
		blockUnblockUser,
		updateUserFraudStatus,
		updateUserLevel,
		updateAlternatePhone,
		cancelSubscription
	};
};

export default useService;
